import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {UserState} from '../../interfaces/UserState';
import {User} from '../../interfaces/User';
import {Firm} from '../../interfaces/Firm';
import {API_BASE_URL} from "../../api/Settings";

// 🔁 Chargement depuis localStorage
const savedUser = localStorage.getItem('user');
const initialUser = savedUser ? JSON.parse(savedUser) : null;

if (initialUser?.user && typeof initialUser.user.avatarIndex !== 'number') {
    initialUser.user.avatarIndex = 0;
    localStorage.setItem('user', JSON.stringify(initialUser));
}

const savedFirm = localStorage.getItem('selectedFirm');
const initialFirm = savedFirm ? JSON.parse(savedFirm) : null;

const initialState: UserState = {
    token: initialUser?.token ?? null,
    user: initialUser?.user ?? null,
    firm: initialFirm ?? null
};

// PATCH /users/me/avatar-index
export const patchAvatarIndex = createAsyncThunk<
    number,           // return type: the avatarIndex we get back
    number,           // arg type: the avatarIndex we want to set
    { state: any }    // to read token from state without circular imports
>(
    'user/patchAvatarIndex',
    async (avatarIndex, thunkAPI) => {
        const token = (thunkAPI.getState() as any)?.user?.token;
        if (!token) throw new Error('Not authenticated');

        const res = await fetch(`${API_BASE_URL}/users/me/avatar-index`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({avatarIndex}),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new Error(data.message || 'Failed to update avatarIndex');
        }
        // Backend returns { idt, avatarIndex }
        return data.avatarIndex as number;
    }
);

function applyUserState(
    state: UserState,
    payload: { user: User; token: string; firm?: Firm }
) {
    state.user = payload.user;
    state.token = payload.token || null;
    state.firm = payload.firm ?? state.firm;

    if (payload.firm) {
        payload.firm.isAdmin =
            payload.firm.role.toUpperCase() === 'ADMIN' || payload.firm.role.toUpperCase() === 'SUPER';
    }

    localStorage.setItem(
        'user',
        JSON.stringify({
            user: payload.user,
            token: payload.token,
            firms: [payload.firm],
        })
    );
    localStorage.setItem('selectedFirm', JSON.stringify(payload.firm));
}


const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        registerUser: (
            state,
            action: PayloadAction<{ user: User; firm: Firm; token: string }>
        ) => {
            console.log('✅ Register success:', action.payload);
            applyUserState(state, action.payload);
        },
        loginSuccess: (
            state,
            action: PayloadAction<{
                user: User;
                token: string;
                firm?: Firm;
            }>
        ) => {
            console.log('✅ Login success:', action.payload);
            applyUserState(state, action.payload);
        },
        logout: (state) => {
            state.token = null;
            state.user = null;
            state.firm = null;
            localStorage.removeItem('user');
            localStorage.removeItem('selectedFirm');
        },
        setFirm: (state, action: PayloadAction<Firm>) => {
            const firm = action.payload;
            state.firm = firm;
            if (firm) {
                firm.isAdmin = firm.role.toUpperCase() === 'ADMIN' || firm.role.toUpperCase() === 'SUPER';
            }
            localStorage.setItem('selectedFirm', JSON.stringify(firm));
        },
        updateAvatarIndex: (state, action: PayloadAction<number>) => {
            if (!state.user) return;
            state.user.avatarIndex = action.payload;

            // persist
            const saved = localStorage.getItem('user');
            const parsed = saved ? JSON.parse(saved) : {token: state.token, user: state.user};
            parsed.user = {...parsed.user, avatarIndex: action.payload};
            localStorage.setItem('user', JSON.stringify(parsed));
        },
    },
    extraReducers: (builder) => {
        builder.addCase(patchAvatarIndex.fulfilled, (state, action) => {
            if (!state.user) return;
            state.user.avatarIndex = action.payload;

            // persist to localStorage to survive reload
            const saved = localStorage.getItem('user');
            const parsed = saved ? JSON.parse(saved) : {token: state.token, user: state.user};
            parsed.user = {...parsed.user, avatarIndex: action.payload};
            localStorage.setItem('user', JSON.stringify(parsed));
        });
        // (optional) handle pending/rejected if you want to show a loading/error state
    },
});

export const {registerUser, loginSuccess, logout, setFirm, updateAvatarIndex} = userSlice.actions;
export default userSlice.reducer;
